import React, { useCallback, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { v4 as uuidv4 } from 'uuid';
import { useApolloClient } from '@apollo/client';
// Types
import { CampaignLocationInput } from 'api/graphql-global-types';
// Api
import { GET_PLACE_DETAILS, GET_PLACE_PREDICTIONS } from 'api/places/queries';
// Helpers
import { PLACE_PRIMARY_TYPES } from 'helpers/campaign';
// UI
import { errorNotification } from 'ui/Notification';

export type LocationOption = {
  label: string;
  value: string;
};

export type LocationSelectProps = {
  id: string;
  value: CampaignLocationInput[];
  onChange: (locations: CampaignLocationInput[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  includedPrimaryTypes?: string[];
  saveFullState?: boolean;
};

const LocationSelect: React.FC<LocationSelectProps> = ({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  className,
  includedPrimaryTypes = [
    // Default to all types
    PLACE_PRIMARY_TYPES.Country,
    PLACE_PRIMARY_TYPES.AdministrativeAreaLevel1,
    PLACE_PRIMARY_TYPES.Locality,
  ],
  saveFullState = false,
}) => {
  const sessionTokenRef = useRef(uuidv4());
  const client = useApolloClient();

  const loadOptions = useCallback(
    async (
      inputValue: string,
      callback: (options: LocationOption[]) => void
    ) => {
      // Add this check for empty input
      if (!inputValue || inputValue.trim().length < 2) {
        callback([]);
        return;
      }

      // compute selectedOptions from latest value
      const selectedOptions: LocationOption[] = value.map((loc) => ({
        label: loc.placeText ?? '',
        value: loc.placeId ?? '',
      }));

      try {
        const { data } = await client.query({
          query: GET_PLACE_PREDICTIONS,
          variables: {
            input: {
              query: inputValue,
              includedPrimaryTypes,
              sessionToken: sessionTokenRef.current,
            },
          },
          fetchPolicy: 'network-only',
        });

        const fetchedOptions =
          data?.getPlacePredictions?.entities.map((e: any) => ({
            label: e.text ?? '',
            value: e.id,
          })) || [];

        // combine previously selected and new options
        const merged = [
          ...selectedOptions,
          ...fetchedOptions.filter(
            (fo: LocationOption) =>
              !selectedOptions.some(
                (so: LocationOption) => so.value === fo.value
              )
          ),
        ];
        callback(merged);
      } catch (err) {
        console.error('Error in loadOptions:', err);
        callback(selectedOptions);
      }
    },
    [client, value, includedPrimaryTypes]
  );

  const handleChange = useCallback(
    async (selected: LocationOption[] | null) => {
      if (!selected) {
        onChange([]);
        return;
      }
      const selectedIds = selected.map((s) => s.value);
      const currentIds = (value || []).map((v) => v.placeId ?? '');

      // remove
      const removed = currentIds.filter((id) => !selectedIds.includes(id));

      // add
      const added = selected.find((s) => !currentIds.includes(s.value));

      if (removed.length) {
        const filtered = (value || []).filter((loc) =>
          selectedIds.includes(loc.placeId ?? '')
        );
        onChange(filtered);
      } else if (added) {
        try {
          const { data } = await client.query({
            query: GET_PLACE_DETAILS,
            variables: {
              input: {
                id: added.value,
                sessionToken: sessionTokenRef.current,
              },
            },
            fetchPolicy: 'network-only',
          });
          const place = data?.getPlaceDetails;
          if (place) {
            const state = saveFullState ? place.state : place.stateCode;

            const newLocation: CampaignLocationInput = {
              placeId: added.value,
              placeText: added.label,
              country: place.countryCode ?? '',
              city: place.city ?? '',
              state: state ?? '',
              latitude: place.location?.latitude ?? null,
              longitude: place.location?.longitude ?? null,
            };
            onChange([...(value || []), newLocation]);
            sessionTokenRef.current = uuidv4();
          }
        } catch (error) {
          errorNotification((error as Error)?.message);
        }
      }
    },
    [value, onChange, client, saveFullState]
  );

  return (
    <AsyncSelect
      id={id}
      isMulti
      isDisabled={disabled}
      placeholder={placeholder}
      className={className}
      value={value?.map((loc) => ({
        label: loc.placeText ?? '',
        value: loc.placeId ?? '',
      }))}
      onChange={handleChange as any}
      loadOptions={loadOptions}
      cacheOptions
      defaultOptions={false}
    />
  );
};

export default LocationSelect;
